import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { services } from '../data/mock';
import { Wrench, Settings, PencilRuler, Phone, ArrowRight, Clock, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const iconMap = {
  'wrench': Wrench,
  'settings': Settings,
  'pencil-ruler': PencilRuler,
  'phone': Phone
};

const Services = () => {
  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Our Services
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Comprehensive commercial refrigeration services from installation to maintenance
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card 
                  key={service.id} 
                  className="bg-gray-900 border-gray-800 hover:border-orange-500/50 transition-all duration-300 group"
                >
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 rounded-full bg-orange-500/10 flex items-center justify-center group-hover:bg-orange-500/20 transition-colors">
                          {IconComponent && <IconComponent className="text-orange-500" size={32} />}
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-orange-500 transition-colors">
                          {service.title}
                        </h3>
                        <p className="text-gray-400 mb-6 leading-relaxed">
                          {service.description}
                        </p>
                        <div className="space-y-2 mb-6">
                          {service.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center space-x-2 text-sm">
                              <CheckCircle size={16} className="text-orange-500 flex-shrink-0" />
                              <span className="text-gray-300">{feature}</span>
                            </div>
                          ))}
                        </div>
                        <Button 
                          asChild 
                          className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
                        >
                          <Link to="/contact">
                            Learn More
                            <ArrowRight className="ml-2" size={16} />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Process */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our Service Process
            </h2>
            <p className="text-gray-400 text-lg">
              Simple, transparent, and efficient
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Initial Contact",
                description: "Reach out via phone, email, or our contact form with your requirements"
              },
              {
                step: "02",
                title: "Assessment",
                description: "Our experts evaluate your needs and provide a detailed quote"
              },
              {
                step: "03",
                title: "Installation/Service",
                description: "Certified technicians complete the work with precision and care"
              },
              {
                step: "04",
                title: "Follow-Up",
                description: "Quality check and ongoing support to ensure your satisfaction"
              }
            ].map((process, index) => (
              <div key={index} className="relative">
                <Card className="bg-gray-800 border-gray-700 h-full hover:border-orange-500/50 transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="text-6xl font-bold text-orange-500/20 mb-4">
                      {process.step}
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">
                      {process.title}
                    </h3>
                    <p className="text-gray-400 text-sm">
                      {process.description}
                    </p>
                  </CardContent>
                </Card>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="text-orange-500" size={24} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Our Services */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Why Choose Our Services?
            </h2>
            <p className="text-gray-400 text-lg">
              Unmatched expertise and customer commitment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Rapid Response",
                description: "Average response time of under 2 hours for emergency calls",
                icon: Clock
              },
              {
                title: "Certified Technicians",
                description: "All technicians are factory-trained and industry certified",
                icon: CheckCircle
              },
              {
                title: "Warranty Protection",
                description: "Comprehensive warranties on all parts and labor",
                icon: Phone
              }
            ].map((benefit, index) => (
              <Card key={index} className="bg-gray-900 border-gray-800 hover:border-orange-500/50 transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-orange-500/10 mb-6">
                    <benefit.icon className="text-orange-500" size={40} />
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-4">{benefit.title}</h3>
                  <p className="text-gray-400">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            Ready to Schedule Service?
          </h2>
          <p className="text-black/80 text-lg mb-8">
            Get in touch with our team today to discuss your refrigeration service needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-black hover:bg-gray-900 text-orange-500 font-bold px-8">
              <Link to="/contact">Schedule Service</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-black text-black hover:bg-black/10 font-bold px-8">
              <a href="tel:772-362-9384">Call 772-362-9384</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
